<template>
  <div class="print-overlay">
    <div class="waiting-mask" v-if="generating">
      <div class="text">
        <h1>{{ $t("printOverlay.pleaseWait") }}</h1>
        <h2>{{ $t("printOverlay.pleaseWaitSubscript") }}</h2>
      </div>
    </div>
    <div class="page-wrapper">
      <div id="page-border">
        <div class="borders">
          <div id="map-area">
            <img
              src="../assets/images/sf_logo.png"
              alt=""
              class="company-logo"
              href="https://www.schubert-franzke.com"
            />
            <img id="qrcode" class="print-qr" />
          </div>
        </div>
        <div class="top-row">
          <input class="title" :placeholder="$t('printOverlay.titlePlaceholder')" v-model="title" />
          <img id="logo-img" :src="hostAddress + project.logo" alt="" crossorigin="anonymous" />
        </div>
        <div class="bottom-row">
          <div class="copyright">&copy; <strong>Schubert & Franzke</strong>, {{ new Date().getFullYear() }}</div>
          <div id="attribution"></div>
          <textarea class="notes" :placeholder="$t('printOverlay.notesPlaceholder')" v-model="notes" />
          <div id="print-scale-bar"></div>
        </div>
      </div>
    </div>
    <div class="button-wrapper">
      <div class="buttons">
        <button id="close" @click="closeFrame">{{ $t("printOverlay.close") }}</button>
        <button id="print" @click="createPrint">{{ $t("printOverlay.print") }}</button>
      </div>
    </div>
  </div>
</template>

<script>
import Vue from "vue";
import M2wApi from "@/services/m2w_api";
import { ScaleLine } from "ol/control";
import { basemap } from "@/services/map/basemap";
import printJS from "print-js";
import jsPDF from "jspdf";
import QRCode from "qrcode";

export default {
  name: "PrintOverlay",
  props: {
    isMtkActive: { type: Boolean, default: false },
    getMtkSnapshot: { type: Function, default: null },
    getPrintUrl: { type: Function, default: null },
    // getMtkUrl: { type: Function, default: null },
  },
  data() {
    return {
      project: Vue.config.project,
      hostAddress: M2wApi.host_address,
      title: null,
      notes: null,
      generating: false,
      qrcode: null,
    };
  },
  mounted() {
    this.scaleLine = new ScaleLine({
      units: "metric",
      bar: true,
      steps: 4,
      text: false,
      target: document.getElementById("print-scale-bar"),
      minWidth: 200,
    });
    basemap.map.addControl(this.scaleLine);

    const attributions = [...document.querySelectorAll(".ol-attribution li")];
    document.getElementById("attribution").innerText =
      attributions.map((a) => a.innerText).join(" | ");

    this.updateQR();
  },
  destroyed() {
    basemap.map.removeControl(this.scaleLine);
  },
  watch: {
    $route() {
      this.updateQR();
    },
  },
  methods: {
    closeFrame() {
      this.$root.$data.printMode = false;
    },
    updateQR() {
      const image = document.getElementById("qrcode");
      if (!image) return;

      const urlForQr = typeof this.getPrintUrl === "function"
        ? this.getPrintUrl({ isMtk: this.isMtkActive })
        : window.location.href;

      QRCode.toDataURL(
        urlForQr,
        { errorCorrectionLevel: "M", margin: 0 },
        (err, url) => {
          if (!err) image.src = url;
        }
      );
    },

    // updateQR() {
    //   const image = document.getElementById("qrcode");
    //   QRCode.toDataURL(
    //     window.location.href,
    //     { errorCorrectionLevel: "M", margin: 0 },
    //     (err, url) => {
    //       if (!err) image.src = url;
    //     }
    //   );
    // },
   async createPrint() {
    this.generating = true;
    try {
      this.updateQR();
      const pageSize = [297, 210]; // A4
      const dpi = 200;
      const pageWidth = Math.round((pageSize[0] * dpi) / 25.4);
      const pageHeight = Math.round((pageSize[1] * dpi) / 25.4);

      let imageUrl;

      if (this.isMtkActive && typeof this.getMtkSnapshot === "function") {
        try {
          imageUrl = await this.getMtkSnapshot({ pxWidth: pageWidth, pxHeight: pageHeight });
        } catch (e) {
          console.warn("[PrintOverlay] MTK snapshot failed, fallback la OL:", e);
          imageUrl = null;
        }
      }

      if (!imageUrl) {
        const pageRect = document.getElementById("map-area").getBoundingClientRect();
        const pageCenter = basemap.map.getCoordinateFromPixel([
          pageRect.x + pageRect.width / 2,
          pageRect.y + pageRect.height / 2,
        ]);

        const mapCanvas = await this.renderMap(pageWidth, pageHeight, pageCenter);
        const context = mapCanvas.getContext("2d");

        const widthRatio = pageRect.width / basemap.map.getSize()[0];
        const desiredWidth = mapCanvas.width * widthRatio;
        const desiredHeight = (desiredWidth / pageRect.width) * pageRect.height;

        const imgData = context.getImageData(
          Math.round((mapCanvas.width - desiredWidth) / 2),
          Math.round((mapCanvas.height - desiredHeight) / 2),
          Math.round(desiredWidth),
          Math.round(desiredHeight)
        );

        imageUrl = this.getImageURL(imgData, desiredWidth, desiredHeight);
      }

      const pdfUrl = this.generatePdfBlobUrl(imageUrl);
      const win = window.open(pdfUrl, "_blank");

      if (win) {
        win.addEventListener("load", () => {
          win.document.title = "map2web - Schubert & Franzke";
          win.focus();
          win.print();
        });
      } else {
        alert("Open PDF-ul de print.");
        try {
          printJS({
            printable: pdfUrl,
            type: "pdf",
            documentTitle: "map2web - Schubert & Franzke"
          });
        } catch (err) {
          console.error("[PrintOverlay] printJS fallback failed:", err);
        }
    }

    setTimeout(() => URL.revokeObjectURL(pdfUrl), 60_000);
  } catch (e) {
    console.error("[PrintOverlay] createPrint failed:", e);
  } finally {
    this.generating = false;
  }
    }
    ,

    renderMap(pageWidth, pageHeight, pageCenter) {
      // codul tău OL 1:1
      const mapSize = basemap.map.getSize();
      const viewResolution = basemap.map.getView().getResolution();
      const viewCenter = basemap.map.getView().getCenter();

      const promise = new Promise((resolve) => {
        basemap.map.once("rendercomplete", function () {
          const targetCanvas = document.createElement("canvas");
          targetCanvas.width = pageWidth;
          targetCanvas.height = pageHeight;
          const canvasContext = targetCanvas.getContext("2d");

          document
            .querySelectorAll(".ol-layer canvas")
            .forEach(function (canvas) {
              if (canvas.width > 0) {
                const opacity = canvas.parentNode.style.opacity;
                canvasContext.globalAlpha =
                  opacity === "" ? 1 : Number(opacity);
                const transform = canvas.style.transform;
                const matrix = transform
                  .match(/^matrix\(([^(]*)\)$/)[1]
                  .split(",")
                  .map(Number);
                canvasContext.setTransform(...matrix);
                canvasContext.drawImage(canvas, 0, 0);
              }
            });

          basemap.map.setSize(mapSize);
          basemap.map.getView().setResolution(viewResolution);
          basemap.map.getView().setCenter(viewCenter);
          resolve(targetCanvas);
        });
      });

      basemap.map.setSize([pageWidth, pageHeight]);
      const scaling = Math.min(pageWidth / mapSize[0], pageHeight / mapSize[1]);
      basemap.map.getView().setResolution(viewResolution / scaling);
      basemap.map.getView().setCenter(pageCenter);

      return promise;
    },

    getImageURL(imgData, width, height) {
      const canvas = document.createElement("canvas");
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext("2d");
      if (imgData instanceof HTMLImageElement) ctx.drawImage(imgData, 0, 0);
      else ctx.putImageData(imgData, 0, 0);
      return canvas.toDataURL();
    },
    generatePdfBlobUrl(imageUrl) {

      const doc = new jsPDF({
        orientation: "landscape",
        unit: "mm",
        format: "a4",
        docBaseUrl: window.location.origin + "/", 
      });

      doc.addFont("/fonts/Quicksand-Regular.ttf", "Quicksand", "normal");
      doc.setFont("Quicksand");

      const pageWidth = 297;
      const pageHeight = 210;

      let logoElement = document.getElementById("logo-img");

      let vh_in_px = logoElement.clientHeight / 5; // we know this element is 5 high, so we can extrapolate 1vh
      let vh_to_mm = 2.5; // 1vh is 2.5mm
      let px_to_mm = vh_to_mm / vh_in_px;

      let logoWidth = logoElement.clientWidth * px_to_mm;
      let logoX = 297 - 5 * vh_to_mm - logoWidth;
      
      doc.addImage(logoElement, "JPEG", logoX, 2.5 * vh_to_mm, logoWidth, 5 * vh_to_mm);


      doc.addImage(
        imageUrl,
        "PNG",
        5 * vh_to_mm,
        10 * vh_to_mm,
        pageWidth - 10 * vh_to_mm,
        pageHeight - 20 * vh_to_mm
      );
      doc.rect(
        5 * vh_to_mm,
        10 * vh_to_mm,
        pageWidth - 10 * vh_to_mm,
        pageHeight - 20 * vh_to_mm
      );

      const sfLogo = document.querySelector("#map-area .company-logo");
      if (sfLogo) {
        doc.addImage(
          sfLogo,
          5.1 * vh_to_mm,
          pageHeight - 10.1 * vh_to_mm - sfLogo.clientHeight * px_to_mm,
          sfLogo.clientWidth * px_to_mm,
          sfLogo.clientHeight * px_to_mm
        );
      }


      const qrcode = document.querySelector("#qrcode");
      if (qrcode) {
        const border = 0.3 * vh_to_mm;
        const qr_width = qrcode.clientWidth * px_to_mm;
        const qr_height = qrcode.clientHeight * px_to_mm;
        const qr_xpos = pageWidth - 5 * vh_to_mm - qr_width;
        const qr_ypos = pageHeight - 10 * vh_to_mm - qr_height;

        doc.setFillColor("#FFFFFF");
        doc.rect(
          qr_xpos - 2 * border,
          qr_ypos - 2 * border,
          qr_width + 2 * border,
          qr_height + 2 * border,
          "FD"
        );
        doc.addImage(
          qrcode,
          qr_xpos - border,
          qr_ypos - border,
          qr_width,
          qr_height
        );
      }

      if (this.title) {
        doc.setFontSize(17);
        doc.text(this.title, 5 * vh_to_mm, 5 * vh_to_mm, { baseline: "middle" });
      }

      if (this.notes) {
        doc.setFontSize(10);
        doc.text(this.notes, 5 * vh_to_mm, pageHeight - 5 * vh_to_mm, {
          baseline: "middle",
          maxWidth: pageWidth / 2,
        });
      }

      const barContainer = document.getElementById("print-scale-bar");
      const bar = barContainer?.firstElementChild;

      if (bar && bar.clientWidth > 0) {
        const barWidth = bar.clientWidth * px_to_mm;
        const barHeight = 10 * px_to_mm;
        const barX = pageWidth - 5 * vh_to_mm - barWidth;
        const barY = pageHeight - 5 * vh_to_mm - barHeight;
        const singleBarWidth = barWidth / 4;

        for (let i = 0; i < 4; i++) {
          const x = barX + singleBarWidth * i;
          doc.rect(x, barY, singleBarWidth, barHeight, i % 2 ? "DF" : "S");
          doc.line(x, barY, x, barY + 15 * px_to_mm);
        }

        doc.line(
          barX + barWidth,
          barY,
          barX + barWidth,
          barY + 15 * px_to_mm
        );

        const stepTexts = document.querySelectorAll(
          "#print-scale-bar .ol-scale-step-text"
        );
        doc.setFontSize(10);
        for (let i = 0; i < 3 && i < stepTexts.length; i++) {
          doc.text(
            stepTexts[i].innerText,
            barX + (i * barWidth) / 2,
            202,
            { baseline: "middle", align: "center" }
          );
        }
      }

      doc.setFontSize(8);
      doc.text(
        "© Schubert & Franzke, " + new Date().getFullYear(),
        5 * vh_to_mm,
        pageHeight - 10 * vh_to_mm + 1,
        { baseline: "top" }
      );

      const attribution = document.getElementById("attribution")?.innerText || "";
      doc.text(
        attribution,
        pageWidth - 5 * vh_to_mm,
        pageHeight - 10 * vh_to_mm + 1,
        { baseline: "top", align: "right" }
      );

      const blob = doc.output("blob");
      return URL.createObjectURL(blob);
    }



    // generateBase64Pdf(imageUrl) {
    //   const doc = new jsPDF("landscape");
    //   doc.addFont("/fonts/Quicksand-Regular.ttf", "Quicksand", "normal");
    //   doc.setFont("Quicksand");

    //   //city logo
    //   const logoElement = document.getElementById("logo-img");

    //   const vh_in_px = logoElement.clientHeight / 5; // 5vh element height
    //   const vh_to_mm = 2.5; // 1vh ~ 2.5mm în layout-ul tău
    //   const px_to_mm = vh_to_mm / vh_in_px;

    //   const logoWidth = logoElement.clientWidth * px_to_mm;
    //   const logoX = 297 - 5 * vh_to_mm - logoWidth;
    //   doc.addImage(logoElement, "JPEG", logoX, 2.5 * vh_to_mm, logoWidth, 5 * vh_to_mm);

    //   // map image
    //   doc.addImage(imageUrl, 5 * vh_to_mm, 10 * vh_to_mm, 297 - 10 * vh_to_mm, 210 - 20 * vh_to_mm);
    //   doc.rect(5 * vh_to_mm, 10 * vh_to_mm, 297 - 10 * vh_to_mm, 210 - 20 * vh_to_mm);

    //   // sf logo overlay
    //   const sfLogo = document.querySelector("#map-area .company-logo");
    //   doc.addImage(
    //     sfLogo,
    //     5.1 * vh_to_mm,
    //     210 - 10.1 * vh_to_mm - sfLogo.clientHeight * px_to_mm,
    //     sfLogo.clientWidth * px_to_mm,
    //     sfLogo.clientHeight * px_to_mm
    //   );

    //   // QR
    //   const qrcode = document.getElementById("qrcode");
    //   const border = 0.3 * vh_to_mm;
    //   const qr_width = qrcode.clientWidth * px_to_mm;
    //   const qr_height = qrcode.clientHeight * px_to_mm;
    //   const qr_xpos = 297 - 5 * vh_to_mm - qr_width;
    //   const qr_ypos = 210 - 10 * vh_to_mm - qr_height;

    //   doc.setFillColor("#FFFFFF");
    //   doc.rect(qr_xpos - 2 * border, qr_ypos - 2 * border, qr_width + 2 * border, qr_height + 2 * border, "FD");
    //   doc.addImage(qrcode, qr_xpos - border, qr_ypos - border, qr_width, qr_height);

    //   // title & notes
    //   if (this.title) {
    //     doc.setFontSize(17);
    //     doc.text(this.title, 5 * vh_to_mm, 5 * vh_to_mm, { baseline: "middle" });
    //   }
    //   if (this.notes) {
    //     doc.setFontSize(10);
    //     doc.text(this.notes, 5 * vh_to_mm, 210 - 5 * vh_to_mm, {
    //       baseline: "middle",
    //       maxWidth: 297 / 2,
    //     });
    //   }

    //   // copyright & attribution
    //   doc.setFontSize(8);
    //   doc.text("© Schubert & Franzke, " + new Date().getFullYear(), 5 * vh_to_mm, 210 - 10 * vh_to_mm + 1, {
    //     baseline: "top",
    //   });
    //   doc.text(document.getElementById("attribution").innerText, 297 - 5 * vh_to_mm, 210 - 10 * vh_to_mm + 1, {
    //     baseline: "top",
    //     align: "right",
    //   });

    //   const outputLink = doc.output("datauristring");
    //   return outputLink.substring(outputLink.indexOf("base64,") + "base64,".length);
    // },
  },
};
</script>



<style scoped></style>
